import React, { useState, useEffect, useCallback } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Select, MenuItem, FormControl,
  InputLabel, Alert, IconButton, Chip, Stack, CircularProgress, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Skeleton,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { isAutheticated } from "../auth/helper";
import { getFilterRules, createFilterRule, deleteFilterRule, getFeedGroups, getFeeds } from "./helper/adminapicall";

const SCOPE_COLOR = { global:"error", group:"warning", feed:"info" };

const FilterRules = () => {
  const { admin, token } = isAutheticated();
  const [tab,    setTab]    = useState(0); // 0=global 1=group 2=feed
  const [rules,  setRules]  = useState([]);
  const [groups, setGroups] = useState([]);
  const [feeds,  setFeeds]  = useState([]);
  const [loading,setLoading]= useState(true);
  const [msg,    setMsg]    = useState({ text:"", severity:"success" });

  // Add dialog
  const [dialog, setDialog] = useState(false);
  const [form,   setForm]   = useState({ type:"ignore_keyword", value:"", scope:"global", group_id:"", feed_id:"" });
  const [saving, setSaving] = useState(false);
  const [fErr,   setFErr]   = useState("");

  // Delete
  const [delTarget, setDelTarget] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const scopes  = ["global","group","feed"];
  const curScope = scopes[tab];

  const flash = (text, severity="success") => { setMsg({text,severity}); setTimeout(()=>setMsg({text:""}),4000); };

  const load = useCallback(() => {
    setLoading(true);
    getFilterRules(admin._id, token, `?scope=${curScope}`).then(d => setRules(Array.isArray(d)?d:[])).finally(()=>setLoading(false));
  }, [admin._id, token, curScope]);

  useEffect(()=>{ load(); },[load]);
  useEffect(()=>{
    getFeedGroups(admin._id, token).then(d=>setGroups(Array.isArray(d)?d:[]));
    getFeeds(admin._id, token, "?limit=200").then(d=>setFeeds(Array.isArray(d?.feeds)?d.feeds:[]));
  },[admin._id, token]);

  const openAdd = () => {
    setForm({ type:"ignore_keyword", value:"", scope:curScope, group_id:"", feed_id:"" });
    setFErr(""); setDialog(true);
  };

  const save = async () => {
    if (!form.value.trim()) return setFErr("Value is required");
    if (form.scope==="group" && !form.group_id) return setFErr("Select a group");
    if (form.scope==="feed"  && !form.feed_id)  return setFErr("Select a feed");
    setSaving(true);
    const body = { type:form.type, value:form.value.trim(), scope:form.scope,
      group_id: form.scope==="group" ? form.group_id : null,
      feed_id:  form.scope==="feed"  ? form.feed_id  : null };
    const d = await createFilterRule(admin._id, token, body);
    setSaving(false);
    if (d?.error) return setFErr(d.error);
    flash("Rule added"); setDialog(false); load();
  };

  const handleDelete = async () => {
    setDeleting(true);
    const d = await deleteFilterRule(admin._id, token, delTarget._id);
    setDeleting(false);
    if (d?.error) return flash(d.error,"error");
    flash("Rule deleted"); setDelTarget(null); load();
  };

  const scopeLabel = curScope.charAt(0).toUpperCase() + curScope.slice(1);

  return (
    <Base>
      {msg.text && <Alert severity={msg.severity} sx={{mb:2}} onClose={()=>setMsg({text:""})}>{msg.text}</Alert>}

      <Box sx={{ mb:3, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:2 }}>
        <Box>
          <Typography variant="h5">Filter Rules</Typography>
          <Typography variant="body2" color="text.secondary">
            Ignore keywords and domains at global, group, or feed level (all levels merge additively)
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon/>} onClick={openAdd}>
          Add {scopeLabel} Rule
        </Button>
      </Box>

      {/* Stats */}
      <Stack direction="row" gap={1.5} sx={{ mb:3, flexWrap:"wrap" }}>
        {["global","group","feed"].map(s=>(
          <Paper key={s} elevation={0} sx={{px:2,py:1,border:"1px solid",borderColor:"divider",borderRadius:2,minWidth:100}}>
            <Typography variant="caption" color="text.secondary" display="block" textTransform="capitalize">{s}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{color:`${SCOPE_COLOR[s]}.main`}}>—</Typography>
          </Paper>
        ))}
      </Stack>

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{mb:2,borderBottom:"1px solid",borderColor:"divider"}}>
        <Tab label="Global rules"  />
        <Tab label="Group rules"   />
        <Tab label="Feed rules"    />
      </Tabs>

      <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider", borderRadius:3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight:600}}>Type</TableCell>
                <TableCell sx={{fontWeight:600}}>Value</TableCell>
                {tab > 0 && <TableCell sx={{fontWeight:600}}>{tab===1?"Group":"Feed"}</TableCell>}
                <TableCell sx={{fontWeight:600}}>Added</TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(5)].map((_,i)=><TableRow key={i}>{[...Array(4)].map((__,j)=><TableCell key={j}><Skeleton/></TableCell>)}</TableRow>)
                : rules.length===0
                  ? <TableRow>
                      <TableCell colSpan={5} align="center" sx={{py:6}}>
                        <FilterAltIcon sx={{fontSize:40,color:"text.disabled",opacity:.3}}/>
                        <Typography variant="body2" color="text.secondary" sx={{mt:1}}>No {curScope} filter rules</Typography>
                      </TableCell>
                    </TableRow>
                  : rules.map(r=>(
                      <TableRow key={r._id} hover>
                        <TableCell>
                          <Chip size="small" label={r.type==="ignore_keyword"?"Keyword":"Domain"}
                            color={r.type==="ignore_keyword"?"warning":"error"} variant="outlined"/>
                        </TableCell>
                        <TableCell><Typography variant="body2" fontFamily="monospace">{r.value}</Typography></TableCell>
                        {tab===1 && <TableCell><Typography variant="body2">{r.group_id?.name||"—"}</Typography></TableCell>}
                        {tab===2 && <TableCell><Typography variant="body2" noWrap sx={{maxWidth:180}}>{r.feed_id?.title||"—"}</Typography></TableCell>}
                        <TableCell><Typography variant="caption" color="text.secondary">{new Date(r.createdAt).toLocaleDateString()}</Typography></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={()=>setDelTarget(r)}><DeleteIcon fontSize="small"/></IconButton>
                        </TableCell>
                      </TableRow>
                    ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add dialog */}
      <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add {scopeLabel} Filter Rule</DialogTitle>
        <DialogContent sx={{display:"flex",flexDirection:"column",gap:2,pt:"16px !important"}}>
          {fErr && <Alert severity="error" sx={{mb:0}}>{fErr}</Alert>}
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={form.type} label="Type" onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
              <MenuItem value="ignore_keyword">Ignore keyword</MenuItem>
              <MenuItem value="ignore_domain">Ignore domain</MenuItem>
            </Select>
          </FormControl>
          <TextField label={form.type==="ignore_keyword"?"Keyword":"Domain"} size="small" fullWidth
            value={form.value} onChange={e=>setForm(p=>({...p,value:e.target.value}))}
            placeholder={form.type==="ignore_keyword"?"sponsored, advertisement…":"msn.com, rediff.com"}/>
          {tab===1 && (
            <FormControl size="small" fullWidth>
              <InputLabel>Group</InputLabel>
              <Select value={form.group_id} label="Group" onChange={e=>setForm(p=>({...p,group_id:e.target.value}))}>
                {groups.map(g=><MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {tab===2 && (
            <FormControl size="small" fullWidth>
              <InputLabel>Feed</InputLabel>
              <Select value={form.feed_id} label="Feed" onChange={e=>setForm(p=>({...p,feed_id:e.target.value}))}>
                {feeds.map(f=><MenuItem key={f._id} value={f._id}>{f.title}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving?<CircularProgress size={16}/>:"Add Rule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      {delTarget && (
        <Dialog open onClose={()=>setDelTarget(null)}>
          <DialogTitle>Delete rule?</DialogTitle>
          <DialogContent><Typography>Remove <b>{delTarget.type.replace("_"," ")}</b>: <code>{delTarget.value}</code>?</Typography></DialogContent>
          <DialogActions>
            <Button onClick={()=>setDelTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}>
              {deleting?<CircularProgress size={16}/>:"Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Base>
  );
};

export default FilterRules;
